import { useCallback, useState } from 'react';
import styled from 'styled-components';
import Logo from '../svg/QualitylodgingLogo';
import { OptionType, SelectInputField } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { useRouter } from 'next/router';
import useGetPageSlugByLocale from '../../hooks/useGetPageSlugByLocale';
import Page from '../Page';
import { Flex } from '@rebass/grid';

const options: OptionType[] = [
    { label: 'nl', value: 'nl' },
    { label: 'en', value: 'en' },
    { label: 'de', value: 'de' },
];

const Navigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const router = useRouter();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const getLink = useCallback(
        (lang: string) => {
            if (lang === router.locale) {
                return router.asPath;
            }
            const accommodationPageSlug = getPageSlugByLocale('ACCOMMODATION', lang);
            const activityPageSlug = getPageSlugByLocale('ACTIVITY', lang);
            const companyPageSlug = getPageSlugByLocale('COMPANY', lang);

            const pathParts = router.asPath.split('/');
            pathParts[0] = lang;
            switch (pathParts[1]) {
                case 'complex':
                    pathParts[1] = 'complex';
                    break;
                case 'accommodatie':
                case 'accommodation':
                case 'unterkunft':
                    pathParts[1] = accommodationPageSlug;
                    break;
                case 'activiteit':
                case 'activity':
                case 'aktivitat':
                    pathParts[1] = activityPageSlug;
                    break;
                case 'onderneming':
                case 'company':
                case 'firma':
                    pathParts[1] = companyPageSlug;
                    break;
                default:
                    break;
            }
            return pathParts.join('/');
        },
        [router.asPath, getPageSlugByLocale, router.locale]
    );

    return (
        <Page>
            <NavWrapper>
                <LogoWrapper
                    onClick={() => {
                        router.push(`https://qualitylodgings.com/${router.locale}`);
                    }}
                >
                    <Logo width={270} height={35} />
                </LogoWrapper>
                <Flex alignItems="center">
                    <LanguageWrapper onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
                        <StyledInput
                            hideChoices
                            styles={{
                                dropdownIndicator: base => ({
                                    ...base,
                                    display: 'none',
                                }),
                            }}
                            thinShadow
                            menuIsOpen={isOpen}
                            value={options.find(language => language.value === router.locale) ?? options[0]}
                            name="Language"
                            options={options.filter(language => language.value !== router.locale) ?? options}
                            isSearchable={false}
                            classNamePrefix="lang"
                            onChange={(option: OptionType) => {
                                const link = getLink(option.value);
                                if (link) {
                                    window.location.href = link.startsWith('/') ? link : `/${link}`;
                                }
                            }}
                        />
                    </LanguageWrapper>
                </Flex>
            </NavWrapper>
        </Page>
    );
};

export default Navigation;

const NavWrapper = styled.nav`
    background-color: white;
    display: flex;
    z-index: 100;
    position: fixed;
    top: 0;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid black;
`;

const StyledInput = styled(SelectInputField)`
    padding-top: 0.2rem;
    width: 50px;
    color: black;
    font-size: 14px;
    text-transform: uppercase;

    [class^='lang__control'] {
        &.lang__control--menu-is-open {
            box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
        }
        box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
        padding-right: 0;
    }
    [class^='lang__menu'] {
        margin-top: 0;
        border-radius: 0 0 0 0;
        border: none;
        border-top: none;
        box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
    }

    [class^='lang__menu-list'] {
        padding-top: 0;
        padding-bottom: 0;
    }

    [class^='lang__option'] {
        color: black;
        &:hover {
            background-color: #789f90;
            color: white;
        }
    }
`;
const LogoWrapper = styled.ul`
    display: flex;
    padding-top: 0.5rem;
    padding-left: 4rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-left: 2rem;
    }
`;

const LanguageWrapper = styled.div`
    display: flex;
    align-items: center;
    padding-right: 2.4rem;
`;
